import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  useToast,
  FormErrorMessage,
  Textarea,
  Badge,
  Icon,
  Card,
  CardBody,
  Flex,
  Spacer,
  Tag,
  TagLabel,
  TagCloseButton,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Image,
  Stack,
  Container,
  Select
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FiUser, FiMail, FiMusic, FiFileText, FiArrowLeft, FiCheck, FiGlobe, FiInstagram, FiTwitter, FiUpload, FiFile, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { useSubscription } from '../../../context/SubscriptionContext';
import ArtistDetailsForm from '../../Auth/ArtistDetailsForm';

const ArtistCreationForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [merchantData, setMerchantData] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const [genres, setGenres] = useState([]);
  const [genreInput, setGenreInput] = useState('');
  const [artistData, setArtistData] = useState(null);
  const [socialMediaHandles, setSocialMediaHandles] = useState([]);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  // Rights documents state
  const [rightsDocumentType, setRightsDocumentType] = useState('');
  const [rightsDocuments, setRightsDocuments] = useState([]);

  const toast = useToast();
  const navigate = useNavigate();
  const { isSwitchedContext } = useAuth();
  const { userSubscription, loading: subscriptionLoading } = useSubscription();

  const steps = [
    { title: 'Basic Info', description: 'Name and contact' },
    { title: 'Platform Verification', description: 'Spotify/Apple Music' },
    { title: 'Profile Details', description: 'Bio and genres' },
    { title: 'Social Media', description: 'Social profiles' },
    { title: 'Rights Documents', description: 'Upload verification' },
    { title: 'Review', description: 'Confirm details' }
  ];

  const [activeStep, setActiveStep] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstname: '',
      lastname: '',
      artist_name: '',
      email: '',
      username: '',
      bio: '',
      genres: [],
      record_label: 'Independent'
    }
  });

  const watchedFields = watch();

  const artistRightsDocumentTypes = [
    { value: 'artist_rights_agreement', label: 'Artist Rights Agreement' },
    { value: 'management_contract', label: 'Management Contract' },
    { value: 'record_label_agreement', label: 'Record Label Agreement' },
    { value: 'publishing_agreement', label: 'Publishing Agreement' },
    { value: 'performance_rights', label: 'Performance Rights' },
    { value: 'master_recording_rights', label: 'Master Recording Rights' }
  ];

  // Check merchant verification status
  useEffect(() => {
    const checkVerification = async () => {
      try {
        const response = await axiosInstance.get('/merchants/profile');
        setMerchantData(response.data);

        if (response.data.verification_status !== 'verified') {
          navigate('/merchant/dashboard', { replace: true });
          toast({
            title: 'Verification Required',
            description: 'Your merchant account must be verified before you can create linked artists.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Error checking verification:', error);
        navigate('/merchant/dashboard', { replace: true });
      } finally {
        setCheckingVerification(false);
      }
    };

    checkVerification();
  }, [navigate, toast]);

  // Block access to artist creation when switched to artist context
  useEffect(() => {
    if (isSwitchedContext) {
      navigate('/dashboard/artist/tracks', { replace: true });
      toast({
        title: 'Access Restricted',
        description: 'You are currently viewing as an artist. Use "Switch back to Merchant" to create artists.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isSwitchedContext, navigate, toast]);

  // Check subscription requirement for merchants
  useEffect(() => {
    if (!subscriptionLoading && !userSubscription) {
      // No subscription at all
      navigate('/subscriptions', { replace: true });
      toast({
        title: 'Subscription Required',
        description: 'You need an active Ultimate subscription to create and manage artists.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } else if (!subscriptionLoading && userSubscription &&
               (userSubscription.plan !== 'ultimate_subscription' || userSubscription.status !== 'active')) {
      // Wrong subscription or inactive
      navigate('/subscriptions', { replace: true });
      toast({
        title: 'Ultimate Subscription Required',
        description: 'Please upgrade to the Ultimate plan to create and manage artists.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [subscriptionLoading, userSubscription, navigate, toast]);

  const addGenre = (genre) => {
    if (genre && !genres.includes(genre) && genres.length < 5) {
      const newGenres = [...genres, genre];
      setGenres(newGenres);
      setValue('genres', newGenres);
    }
    setGenreInput('');
  };

  const removeGenre = (genreToRemove) => {
    const newGenres = genres.filter(genre => genre !== genreToRemove);
    setGenres(newGenres);
    setValue('genres', newGenres);
  };

  const handleGenreInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addGenre(genreInput);
    }
  };

  const handleRightsDocumentsChange = (event) => {
    const files = Array.from(event.target.files);
    setRightsDocuments(files);
  };

  const addSocialMediaHandle = (platform, handle, url) => {
    if (handle && !socialMediaHandles.find(sm => sm.platform === platform)) {
      const newHandles = [...socialMediaHandles, { platform, handle, url }];
      setSocialMediaHandles(newHandles);
    }
  };

  const removeSocialMediaHandle = (platform) => {
    setSocialMediaHandles(prev => prev.filter(sm => sm.platform !== platform));
  };

  const nextStep = async () => {
    if (activeStep === 0) {
      // Validate basic info
      const isValid = await trigger(['firstname', 'lastname', 'artist_name', 'email']);
      if (!isValid) return;
    }
    if (activeStep === 1 && !artistData) {
      toast({
        title: 'Platform Verification Required',
        description: 'Please verify the artist using Spotify or Apple Music',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (activeStep === 4) {
      // Validate rights documents
      if (!rightsDocumentType || rightsDocuments.length === 0) {
        toast({
          title: 'Rights Documents Required',
          description: 'Please select document type and upload at least one document',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const prevStep = () => {
    setActiveStep(prev => prev - 1);
  };

  const onSubmit = async (data) => {
    // Double-check subscription before submission
    if (!userSubscription || userSubscription.plan !== 'ultimate_subscription' || userSubscription.status !== 'active') {
      toast({
        title: 'Ultimate Subscription Required',
        description: 'Please upgrade to the Ultimate plan to create and manage artists.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/subscriptions');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();

      // Create artist data object and send as JSON Body with files
      const artistDataObj = {
        firstname: data.firstname,
        lastname: data.lastname,
        artist_name: data.artist_name,
        email: data.email,
        username: data.username || null,
        record_label: data.record_label || 'Independent',
        image_url: artistData?.image_url || 'https://via.placeholder.com/300x300?text=Artist',
        spotify_popularity: artistData?.spotify_popularity || null,
        bio: data.bio || null,
        genres: genres,
        social_media_handles: socialMediaHandles,
        artist_id: artistData?.artist_id ? [artistData.artist_id] : []
      };

      // Add artist data as a JSON string field
      formData.append('artist_data', JSON.stringify(artistDataObj));

      // Add rights document type and files
      formData.append('rights_document_type', rightsDocumentType);
      rightsDocuments.forEach(file => {
        formData.append('rights_documents', file);
      });

      const response = await axiosInstance.post('/merchants/artists/create-full', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: 'Artist Created Successfully!',
        description: `${data.artist_name} has been created with full profile. Temporary password: ${response.data.artist.temporary_password}`,
        status: 'success',
        duration: 10000,
        isClosable: true,
      });

      navigate('/merchant/artists');

    } catch (error) {
      console.error('Artist creation error:', error);

      let errorMessage = 'Failed to create artist. Please try again.';
      let errorTitle = 'Error Creating Artist';

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle different error response formats
        if (typeof detail === 'string') {
          errorMessage = detail;

          // Special handling for specific error types
          if (detail.includes('email already exists')) {
            errorTitle = 'Email Already In Use';
            errorMessage = 'An artist with this email already exists. Please use a different email address.';
          } else if (detail.includes('username already taken')) {
            errorTitle = 'Username Not Available';
            errorMessage = 'This username is already taken. Please choose a different username.';
          } else if (detail.includes('subscription') || detail.includes('Ultimate')) {
            // Subscription error - redirect without showing toast (frontend already handled it)
            navigate('/subscriptions');
            return;
          }
        } else if (Array.isArray(detail)) {
          // Handle Pydantic validation errors array
          errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        } else if (detail.msg) {
          // Handle single Pydantic validation error object
          errorMessage = detail.msg;
        }
      } else if (error.request) {
        // Network error
        errorTitle = 'Network Error';
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/merchant/dashboard');
  };

  const renderBasicInfoStep = () => (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
          Basic Information
        </Heading>
        <Text color="primary.900" fontSize="sm">
          Enter the artist's personal details to create their account
        </Text>
      </Box>

      <Stack direction={{ base: "column", md: "row" }} spacing={{ base: 3, md: 4 }}>
        <FormControl isInvalid={errors.firstname} isRequired>
          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
            First Name
          </FormLabel>
          <Input
            placeholder="Artist's first name"
            size="lg"
            variant="outline"
            bg="secondary.900"
            color="primary.100"
            _placeholder={{ color: "primary.900" }}
            {...register('firstname', {
              required: 'First name is required',
              minLength: { value: 1, message: 'First name is required' }
            })}
          />
          <FormErrorMessage color="red.400">
            {errors.firstname?.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.lastname} isRequired>
          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
            Last Name
          </FormLabel>
          <Input
            placeholder="Artist's last name"
            size="lg"
            variant="outline"
            bg="secondary.900"
            color="primary.100"
            _placeholder={{ color: "primary.900" }}
            {...register('lastname', {
              required: 'Last name is required',
              minLength: { value: 1, message: 'Last name is required' }
            })}
          />
          <FormErrorMessage color="red.400">
            {errors.lastname?.message}
          </FormErrorMessage>
        </FormControl>
      </Stack>

      <FormControl isInvalid={errors.artist_name} isRequired>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiUser} color="dark.900" />
            <Text>Artist Name</Text>
          </HStack>
        </FormLabel>
        <Input
          placeholder="Enter the artist's stage name"
          size="lg"
          variant="outline"
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
          {...register('artist_name', {
            required: 'Artist name is required',
            minLength: { value: 2, message: 'Artist name must be at least 2 characters' }
          })}
        />
        <FormErrorMessage color="red.400">
          {errors.artist_name?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={errors.email} isRequired>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiMail} color="dark.900" />
            <Text>Email Address</Text>
          </HStack>
        </FormLabel>
        <Input
          type="email"
          placeholder="artist@example.com"
          size="lg"
          variant="outline"
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
        />
        <FormErrorMessage color="red.400">
          {errors.email?.message}
        </FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={errors.username}>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          Username (Optional)
        </FormLabel>
        <Input
          placeholder="Unique username for the artist"
          size="lg"
          variant="outline"
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
          {...register('username', {
            minLength: { value: 3, message: 'Username must be at least 3 characters' },
            pattern: {
              value: /^[a-zA-Z0-9_-]+$/,
              message: 'Username can only contain letters, numbers, hyphens, and underscores'
            }
          })}
        />
        <FormErrorMessage color="red.400">
          {errors.username?.message}
        </FormErrorMessage>
        <Text fontSize="xs" color="primary.900" mt={1}>
          Leave empty to generate automatically
        </Text>
      </FormControl>
    </VStack>
  );

  const renderPlatformVerificationStep = () => (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
          Platform Verification
        </Heading>
        <Text color="primary.900" fontSize="sm">
          Verify the artist using Spotify or Apple Music to get their profile information
        </Text>
      </Box>

      <ArtistDetailsForm artistData={artistData} setArtistData={setArtistData} />

      {!artistData && (
        <Alert status="info" bg="secondary.900" borderColor="dark.900" color="primary.100">
          <AlertIcon color="dark.900" />
          <AlertDescription color="primary.900">
            Platform verification helps populate the artist's profile with accurate information
            including genres, popularity metrics, and profile images.
          </AlertDescription>
        </Alert>
      )}
    </VStack>
  );

  const renderProfileDetailsStep = () => (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
          Profile Details
        </Heading>
        <Text color="primary.900" fontSize="sm">
          Add music genres, biography, and record label information
        </Text>
      </Box>

      {/* Genres */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiMusic} color="dark.900" />
            <Text>Music Genres</Text>
          </HStack>
        </FormLabel>
        <HStack spacing={{ base: 2, md: 3 }} flexWrap={{ base: "wrap", md: "nowrap" }}>
          <Input
            placeholder="Type a genre and press Enter"
            size={{ base: "md", md: "lg" }}
            value={genreInput}
            onChange={(e) => setGenreInput(e.target.value)}
            onKeyDown={handleGenreInputKeyPress}
            isDisabled={genres.length >= 5}
            variant="outline"
            bg="secondary.900"
            color="primary.100"
            _placeholder={{ color: "primary.900" }}
          />
          <Button
            onClick={() => addGenre(genreInput)}
            isDisabled={!genreInput || genres.length >= 5}
            size={{ base: "md", md: "lg" }}
            variant="outline"
            borderColor="dark.900"
            color="dark.900"
            _hover={{ bg: "dark.900", color: "primary.100" }}
            flexShrink={0}
          >
            Add
          </Button>
        </HStack>

        {genres.length > 0 && (
          <HStack wrap="wrap" spacing={2} mt={2}>
            {genres.map((genre, index) => (
              <Tag key={index} size="md" bg="dark.900" color="primary.100" borderRadius="full">
                <TagLabel>{genre}</TagLabel>
                <TagCloseButton onClick={() => removeGenre(genre)} />
              </Tag>
            ))}
          </HStack>
        )}

        <Text fontSize="xs" color="primary.900" mt={1}>
          Add up to 5 genres that best describe this artist's music
        </Text>
      </FormControl>

      {/* Bio */}
      <FormControl isInvalid={errors.bio}>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiFileText} color="dark.900" />
            <Text>Artist Biography</Text>
          </HStack>
        </FormLabel>
        <Textarea
          placeholder="Tell us about this artist..."
          rows={{ base: 3, md: 4 }}
          resize="vertical"
          variant="outline"
          size={{ base: "md", md: "lg" }}
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
          {...register('bio', {
            maxLength: { value: 1000, message: 'Bio must be less than 1000 characters' }
          })}
        />
        <FormErrorMessage color="red.400">
          {errors.bio?.message}
        </FormErrorMessage>
        <Text fontSize="xs" color="primary.900" mt={1}>
          {watchedFields.bio?.length || 0}/1000 characters
        </Text>
      </FormControl>

      {/* Record Label */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          Record Label
        </FormLabel>
        <Input
          placeholder="Independent"
          size="lg"
          variant="outline"
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
          {...register('record_label')}
        />
      </FormControl>
    </VStack>
  );

  const renderSocialMediaStep = () => {
    return (
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        <Box>
          <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
            Social Media Profiles
          </Heading>
          <Text color="primary.900" fontSize="sm">
            Add social media profiles to help fans connect with this artist (Optional)
          </Text>
        </Box>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
            <HStack spacing={2}>
              <Icon as={FiInstagram} color="dark.900" />
              <Text>Instagram</Text>
            </HStack>
          </FormLabel>
          <HStack>
            <Input
              placeholder="@username"
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value)}
              size="lg"
              variant="outline"
              bg="secondary.900"
              color="primary.100"
              _placeholder={{ color: "primary.900" }}
            />
            <Button
              onClick={() => {
                if (instagramHandle) {
                  addSocialMediaHandle('instagram', instagramHandle, `https://instagram.com/${instagramHandle.replace('@', '')}`);
                  setInstagramHandle('');
                }
              }}
              size="lg"
              variant="outline"
              borderColor="dark.900"
              color="dark.900"
              _hover={{ bg: "dark.900", color: "primary.100" }}
              isDisabled={!instagramHandle || socialMediaHandles.find(sm => sm.platform === 'instagram')}
            >
              Add
            </Button>
          </HStack>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
            <HStack spacing={2}>
              <Icon as={FiTwitter} color="dark.900" />
              <Text>Twitter</Text>
            </HStack>
          </FormLabel>
          <HStack>
            <Input
              placeholder="@username"
              value={twitterHandle}
              onChange={(e) => setTwitterHandle(e.target.value)}
              size="lg"
              variant="outline"
              bg="secondary.900"
              color="primary.100"
              _placeholder={{ color: "primary.900" }}
            />
            <Button
              onClick={() => {
                if (twitterHandle) {
                  addSocialMediaHandle('twitter', twitterHandle, `https://twitter.com/${twitterHandle.replace('@', '')}`);
                  setTwitterHandle('');
                }
              }}
              size="lg"
              variant="outline"
              borderColor="dark.900"
              color="dark.900"
              _hover={{ bg: "dark.900", color: "primary.100" }}
              isDisabled={!twitterHandle || socialMediaHandles.find(sm => sm.platform === 'twitter')}
            >
              Add
            </Button>
          </HStack>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
            <HStack spacing={2}>
              <Icon as={FiGlobe} color="dark.900" />
              <Text>Website</Text>
            </HStack>
          </FormLabel>
          <HStack>
            <Input
              placeholder="https://artist-website.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              size="lg"
              variant="outline"
              bg="secondary.900"
              color="primary.100"
              _placeholder={{ color: "primary.900" }}
            />
            <Button
              onClick={() => {
                if (websiteUrl) {
                  addSocialMediaHandle('website', websiteUrl, websiteUrl);
                  setWebsiteUrl('');
                }
              }}
              size="lg"
              variant="outline"
              borderColor="dark.900"
              color="dark.900"
              _hover={{ bg: "dark.900", color: "primary.100" }}
              isDisabled={!websiteUrl || socialMediaHandles.find(sm => sm.platform === 'website')}
            >
              Add
            </Button>
          </HStack>
        </FormControl>

        {socialMediaHandles.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="dark.900" mb={2}>Added Social Media:</Text>
            <VStack spacing={2} align="stretch">
              {socialMediaHandles.map((social, index) => (
                <HStack key={index} p={3} bg="secondary.900" borderRadius="md" borderWidth="1px" borderColor="dark.900">
                  <Text fontSize="sm" fontWeight="medium" textTransform="capitalize" color="primary.100">
                    {social.platform}:
                  </Text>
                  <Text fontSize="sm" color="primary.900">{social.handle}</Text>
                  <Spacer />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="red.400"
                    _hover={{ bg: "red.400", color: "primary.100" }}
                    onClick={() => removeSocialMediaHandle(social.platform)}
                  >
                    Remove
                  </Button>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    );
  };

  const renderRightsDocumentsStep = () => (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
          Artist Rights Documents
        </Heading>
        <Text color="primary.900" fontSize="sm">
          Upload documents that prove you have rights to represent this artist
        </Text>
      </Box>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiFileText} color="dark.900" />
            <Text>Document Type</Text>
          </HStack>
        </FormLabel>
        <Select
          placeholder="Select document type"
          value={rightsDocumentType}
          onChange={(e) => setRightsDocumentType(e.target.value)}
          size="lg"
          variant="outline"
          bg="secondary.900"
          color="primary.100"
          _placeholder={{ color: "primary.900" }}
        >
          {artistRightsDocumentTypes.map((type) => (
            <option key={type.value} value={type.value} style={{ background: '#2A2A2A', color: '#FFFFFF' }}>
              {type.label}
            </option>
          ))}
        </Select>
        <Text fontSize="xs" color="primary.900" mt={1}>
          Choose the type of document that proves your rights over this artist
        </Text>
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
          <HStack spacing={2}>
            <Icon as={FiUpload} color="dark.900" />
            <Text>Upload Documents</Text>
          </HStack>
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor="secondary.700"
          borderRadius={{ base: "md", md: "lg" }}
          p={{ base: 4, md: 6 }}
          bg="secondary.900"
          transition="all 0.2s"
          _hover={{ borderColor: "dark.900" }}
        >
          <VStack spacing={{ base: 3, md: 4 }}>
            <Icon as={FiUpload} boxSize={{ base: 6, md: 8 }} color="primary.900" />
            <VStack spacing={{ base: 1, md: 2 }} textAlign="center">
              <Text fontSize={{ base: "sm", md: "md" }} color="primary.100" fontWeight="medium">
                Upload Rights Documents
              </Text>
              <Text fontSize={{ base: "xs", sm: "sm" }} color="primary.900" px={{ base: 2, md: 0 }}>
                Upload documents that prove your rights to represent this artist
              </Text>
              <Text fontSize={{ base: "xs", sm: "xs" }} color="primary.900" px={{ base: 2, md: 0 }}>
                Accepted formats: PDF, DOC, DOCX, JPG, PNG (max 10MB each)
              </Text>
            </VStack>
            <Input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleRightsDocumentsChange}
              style={{ display: 'none' }}
              id="rights-documents-input"
            />
            <Button
              as="label"
              htmlFor="rights-documents-input"
              variant="outline"
              size={{ base: "md", md: "lg" }}
              leftIcon={<Icon as={FiUpload} boxSize={{ base: 4, md: 5 }} />}
              borderColor="dark.900"
              color="dark.900"
              _hover={{ bg: "dark.900", color: "primary.100" }}
              cursor="pointer"
              fontSize={{ base: "sm", md: "md" }}
              px={{ base: 4, md: 6 }}
              w={{ base: "full", sm: "auto" }}
            >
              Choose Files
            </Button>
          </VStack>
        </Box>

        {rightsDocuments.length > 0 && (
          <Box mt={{ base: 3, md: 4 }} p={{ base: 3, md: 4 }} bg="dark.500" borderRadius={{ base: "md", md: "lg" }} border="1px" borderColor="secondary.700">
            <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="medium" color="primary.100" mb={{ base: 2, md: 3 }}>
              Selected Files ({rightsDocuments.length})
            </Text>
            <VStack spacing={{ base: 1.5, md: 2 }} align="stretch">
              {rightsDocuments.map((file, index) => (
                <Box key={index} p={{ base: 2, md: 3 }} bg="secondary.900" borderRadius="md" border="1px" borderColor="secondary.700">
                  <HStack spacing={{ base: 2, md: 3 }}>
                    <Icon as={FiFile} color="primary.100" boxSize={{ base: 4, md: 5 }} flexShrink={0} />
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text
                        fontSize={{ base: "xs", sm: "sm" }}
                        color="primary.100"
                        fontWeight="medium"
                        noOfLines={1}
                        wordBreak="break-all"
                      >
                        {file.name}
                      </Text>
                      <Text fontSize={{ base: "xs", sm: "xs" }} color="primary.900">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                    </VStack>
                    <Button
                      size={{ base: "xs", sm: "sm" }}
                      variant="ghost"
                      color="red.400"
                      _hover={{ bg: "red.400", color: "primary.100" }}
                      onClick={() => {
                        const newFiles = rightsDocuments.filter((_, i) => i !== index);
                        setRightsDocuments(newFiles);
                      }}
                      flexShrink={0}
                      p={{ base: 1, sm: 2 }}
                    >
                      <Icon as={FiX} boxSize={{ base: 3, sm: 4 }} />
                    </Button>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <Text fontSize="xs" color="primary.900" mt={1}>
          You can upload multiple files. All documents will be reviewed by our admin team.
        </Text>
      </FormControl>

      {rightsDocumentType && rightsDocuments.length > 0 && (
        <Alert status="success" bg="secondary.900" borderColor="green.400" color="primary.100">
          <AlertIcon color="green.400" />
          <AlertDescription color="primary.100">
            Ready to upload {rightsDocuments.length} document(s) of type "{artistRightsDocumentTypes.find(t => t.value === rightsDocumentType)?.label}"
          </AlertDescription>
        </Alert>
      )}

      <Alert status="info" bg="secondary.900" borderColor="dark.900" color="primary.100">
        <AlertIcon color="dark.900" />
        <AlertDescription color="primary.900">
          These documents will be reviewed by our admin team to verify your rights to represent this artist.
          The artist account will be created but marked as pending verification until approved.
        </AlertDescription>
      </Alert>
    </VStack>
  );

  const renderReviewStep = () => (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Heading size="md" color="dark.900" fontFamily="heading" mb={2}>
          Review Artist Details
        </Heading>
        <Text color="primary.900" fontSize="sm">
          Review all information before creating the artist account
        </Text>
      </Box>

      <Box bg="secondary.900" p={6} borderRadius="lg" borderWidth="1px" borderColor="dark.900">
        <VStack spacing={4} align="stretch">
          <HStack>
            {artistData?.image_url && (
              <Image
                src={artistData.image_url}
                alt={watchedFields.artist_name}
                boxSize="80px"
                borderRadius="md"
                border="2px solid"
                borderColor="dark.900"
              />
            )}
            <VStack align="start">
              <Text fontSize="lg" fontWeight="bold" color="primary.100">
                {watchedFields.artist_name}
              </Text>
              <Text fontSize="sm" color="primary.900">
                {watchedFields.firstname} {watchedFields.lastname}
              </Text>
              <Text fontSize="sm" color="primary.900">{watchedFields.email}</Text>
            </VStack>
          </HStack>

          <Divider borderColor="dark.900" />

          {genres.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="primary.100" mb={2}>Genres:</Text>
              <HStack wrap="wrap" spacing={2}>
                {genres.map((genre, index) => (
                  <Badge key={index} bg="dark.900" color="primary.100">{genre}</Badge>
                ))}
              </HStack>
            </Box>
          )}

          {watchedFields.bio && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="primary.100" mb={2}>Bio:</Text>
              <Text fontSize="sm" color="primary.900">{watchedFields.bio}</Text>
            </Box>
          )}

          {socialMediaHandles.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="primary.100" mb={2}>Social Media:</Text>
              <VStack spacing={1} align="start">
                {socialMediaHandles.map((social, index) => (
                  <Text key={index} fontSize="sm" color="primary.900">
                    {social.platform}: {social.handle}
                  </Text>
                ))}
              </VStack>
            </Box>
          )}

          <Alert status="info" bg="dark.900" borderColor="primary.900">
            <AlertIcon color="primary.100" />
            <AlertDescription fontSize="sm" color="primary.100">
              A secure temporary password will be generated for this artist.
              They can update their profile and password after creation.
            </AlertDescription>
          </Alert>
        </VStack>
      </Box>
    </VStack>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicInfoStep();
      case 1:
        return renderPlatformVerificationStep();
      case 2:
        return renderProfileDetailsStep();
      case 3:
        return renderSocialMediaStep();
      case 4:
        return renderRightsDocumentsStep();
      case 5:
        return renderReviewStep();
      default:
        return renderBasicInfoStep();
    }
  };

  // Show loading while checking subscription
  if (subscriptionLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" minH="100vh">
        <VStack spacing={4}>
          <Progress size="sm" colorScheme="blue" isIndeterminate w="200px" />
          <Text color="primary.500">Verifying subscription...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" justifyContent="center" minH="100vh" p={{ base: 1, sm: 2, md: 4 }}>
      <Container maxW="4xl" centerContent>
        <Flex
          direction="column"
          alignItems="center"
          bg="dark.500"
          p={{ base: 3, sm: 4, md: 6, lg: 8 }}
          rounded={{ base: "md", md: "lg" }}
          maxW={{ base: "100vw", sm: "full", md: "2xl", lg: "4xl" }}
          w="full"
          boxShadow="dark-lg"
          border="1px solid"
          borderColor="dark.900"
          mx={{ base: 0, sm: "auto" }}
        >
          <VStack spacing={{ base: 4, md: 6, lg: 8 }} align="stretch" w="full">
            {/* Header */}
            <HStack w="full">
              <Button
                leftIcon={<Icon as={FiArrowLeft} boxSize={{ base: 3, md: 4 }} />}
                variant="ghost"
                onClick={handleBack}
                size={{ base: "xs", sm: "sm", md: "md" }}
                color="primary.900"
                _hover={{ bg: "secondary.900", color: "primary.100" }}
                fontSize={{ base: "xs", sm: "sm" }}
                px={{ base: 2, sm: 3 }}
              >
                <Text display={{ base: "none", sm: "block" }}>Back to Dashboard</Text>
                <Text display={{ base: "block", sm: "none" }}>Back</Text>
              </Button>
              <Spacer />
            </HStack>

            {/* Header Section */}
            <VStack spacing={{ base: 2, md: 3 }} textAlign="center">
              <Box
                p={{ base: 3, md: 4 }}
                rounded="full"
                bg="dark.900"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={FiUser} w={{ base: 6, md: 8 }} h={{ base: 6, md: 8 }} color="primary.100" />
              </Box>

              <VStack spacing={{ base: 1, md: 2 }}>
                <Heading
                  size={{ base: "md", md: "lg" }}
                  color="dark.900"
                  fontFamily="heading"
                  fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
                >
                  Create New Artist Account
                </Heading>
                <Text
                  color="primary.900"
                  fontSize={{ base: "sm", md: "md" }}
                  textAlign="center"
                  maxW={{ base: "full", md: "md" }}
                  px={{ base: 2, md: 0 }}
                >
                  Create a complete artist account with platform integration
                </Text>
              </VStack>

              <Divider borderColor="dark.900" />

              {/* Custom Step Header */}
              <Box w="full" mt={{ base: 4, md: 6 }}>
                {/* Mobile Step Indicator */}
                <Box display={{ base: "block", md: "none" }} mb={4}>
                  <Card bg="secondary.900" borderColor="dark.900" borderWidth="1px">
                    <CardBody p={4}>
                      <VStack spacing={3}>
                        <HStack w="full" justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="primary.900" fontWeight="medium">
                              Step {activeStep + 1} of {steps.length}
                            </Text>
                            <Text fontSize="lg" color="dark.900" fontWeight="bold">
                              {steps[activeStep].title}
                            </Text>
                            <Text fontSize="sm" color="primary.900">
                              {steps[activeStep].description}
                            </Text>
                          </VStack>
                          <Box
                            w={12}
                            h={12}
                            bg="dark.900"
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                          >
                            <Text color="primary.100" fontWeight="bold" fontSize="lg">
                              {activeStep + 1}
                            </Text>
                          </Box>
                        </HStack>
                        <Progress
                          value={(activeStep + 1) * (100 / steps.length)}
                          size="md"
                          borderRadius="full"
                          bg="dark.500"
                          w="full"
                          sx={{
                            '& > div': {
                              bg: 'dark.900'
                            }
                          }}
                        />
                      </VStack>
                    </CardBody>
                  </Card>
                </Box>

                {/* Desktop Step Navigation */}
                <Box display={{ base: "none", md: "block" }}>
                  <HStack spacing={0} justify="space-between" w="full">
                    {steps.map((step, index) => (
                      <React.Fragment key={index}>
                        <VStack
                          spacing={2}
                          align="center"
                          flex={1}
                          opacity={activeStep >= index ? 1 : 0.5}
                          transition="all 0.2s"
                        >
                          <Box
                            w={10}
                            h={10}
                            bg={activeStep >= index ? "dark.900" : "secondary.900"}
                            borderRadius="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            border="2px solid"
                            borderColor={activeStep >= index ? "dark.900" : "secondary.700"}
                            transition="all 0.2s"
                          >
                            {activeStep > index ? (
                              <Icon as={FiCheck} color="primary.100" boxSize={5} />
                            ) : (
                              <Text
                                color={activeStep >= index ? "primary.100" : "primary.900"}
                                fontWeight="bold"
                                fontSize="sm"
                              >
                                {index + 1}
                              </Text>
                            )}
                          </Box>
                          <VStack spacing={1} align="center">
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color={activeStep >= index ? "dark.900" : "primary.900"}
                              textAlign="center"
                              noOfLines={1}
                            >
                              {step.title}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="primary.900"
                              textAlign="center"
                              noOfLines={2}
                              maxW="120px"
                            >
                              {step.description}
                            </Text>
                          </VStack>
                        </VStack>
                        {index < steps.length - 1 && (
                          <Box
                            flex={1}
                            h="2px"
                            bg={activeStep > index ? "dark.900" : "secondary.900"}
                            mx={2}
                            borderRadius="full"
                            transition="all 0.2s"
                            mt="-20px"
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </HStack>
                </Box>
              </Box>
            </VStack>

            {/* Form Content */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={{ base: 6, md: 8 }} w="full">
                {/* Step Content Card */}
                <Card
                  bg="transparent"
                  borderColor="secondary.900"
                  borderWidth="1px"
                  shadow="lg"
                  w="full"
                  borderRadius={{ base: "md", md: "lg" }}
                >
                  <CardBody p={{ base: 3, sm: 4, md: 6, lg: 8 }}>
                    {renderStepContent()}
                  </CardBody>
                </Card>

                {/* Navigation Buttons */}
                <Card bg="dark.500" borderColor="secondary.900" borderWidth="1px" w="full" borderRadius={{ base: "md", md: "lg" }}>
                  <CardBody p={{ base: 3, sm: 4, md: 6 }}>
                    <VStack spacing={{ base: 3, md: 4 }} w="full">
                      <Flex
                        direction={{ base: "column", sm: "row" }}
                        spacing={{ base: 2, sm: 3, md: 4 }}
                        w="full"
                        justify="space-between"
                        gap={{ base: 2, sm: 3, md: 4 }}
                      >
                        {activeStep > 0 ? (
                          <Button
                            onClick={prevStep}
                            variant="outline"
                            size={{ base: "sm", sm: "md", md: "lg" }}
                            isDisabled={isLoading}
                            color="primary.900"
                            borderColor="secondary.900"
                            leftIcon={<Icon as={FiArrowLeft} boxSize={{ base: 3, md: 4 }} />}
                            _hover={{ bg: "secondary.900", color: "primary.100" }}
                            flex={{ base: "none", sm: "none" }}
                            w={{ base: "full", sm: "auto" }}
                            fontSize={{ base: "xs", sm: "sm" }}
                            order={{ base: 2, sm: 1 }}
                          >
                            Previous
                          </Button>
                        ) : (
                          <Box display={{ base: "none", sm: "block" }} />
                        )}

                        {activeStep < steps.length - 1 ? (
                          <Button
                            onClick={nextStep}
                            variant="brand"
                            size={{ base: "sm", sm: "md", md: "lg" }}
                            rightIcon={<Icon as={FiArrowLeft} transform="rotate(180deg)" boxSize={{ base: 3, md: 4 }} />}
                            _hover={{
                              transform: "translateY(-1px)",
                              boxShadow: "lg"
                            }}
                            transition="all 0.2s"
                            flex={{ base: "none", sm: "none" }}
                            w={{ base: "full", sm: "auto" }}
                            ml={{ base: 0, sm: "auto" }}
                            fontSize={{ base: "xs", sm: "sm" }}
                            order={{ base: 1, sm: 2 }}
                          >
                            Next Step
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            variant="brand"
                            size={{ base: "sm", sm: "md", md: "lg" }}
                            isLoading={isLoading}
                            loadingText="Creating Artist Account..."
                            leftIcon={<Icon as={FiCheck} boxSize={{ base: 3, md: 4 }} />}
                            _hover={{
                              transform: "translateY(-1px)",
                              boxShadow: "lg"
                            }}
                            transition="all 0.2s"
                            flex={{ base: "none", sm: "none" }}
                            w={{ base: "full", sm: "auto" }}
                            ml={{ base: 0, sm: "auto" }}
                            fontSize={{ base: "xs", sm: "sm" }}
                            order={{ base: 1, sm: 2 }}
                          >
                            <Text display={{ base: "none", sm: "block" }}>Create Artist Account</Text>
                            <Text display={{ base: "block", sm: "none" }}>Create Artist</Text>
                          </Button>
                        )}
                      </Flex>

                      <Divider borderColor="secondary.900" />

                      {/* Cancel Button */}
                      <Button
                        variant="ghost"
                        size={{ base: "xs", sm: "sm", md: "md" }}
                        onClick={handleBack}
                        isDisabled={isLoading}
                        w="full"
                        color="primary.900"
                        _hover={{
                          color: "dark.900",
                          bg: "transparent"
                        }}
                        fontSize={{ base: "xs", sm: "sm" }}
                        py={{ base: 2, sm: 3 }}
                      >
                        <Text display={{ base: "none", sm: "block" }}>Cancel & Return to Dashboard</Text>
                        <Text display={{ base: "block", sm: "none" }}>Cancel</Text>
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </form>
          </VStack>
        </Flex>
      </Container>
    </Flex>
  );
};

export default ArtistCreationForm;